/**
 * The standing notice a restaurant can put in front of a customer — FRONTEND.md §9.187, §9.328.
 *
 * **Originally two notices, and that turned out to be one too many.** §9.187 found that two settings
 * screens each write one, the server publishes both, and the customer app drew neither:
 *
 * * `/settings/general` → `announcement`, `announcementEnabled` - handled here
 * * `/settings/opening-hours` → `announcement: { enabled, message, showAlways }` - **not** here
 *
 * `hoursNotice` and `customerNotices` used to live in this file and are **gone - §9.328.** The owner
 * was seeing the opening-hours notice twice, because `customer-core`'s `announcementOf` /
 * `announcementShows` already implemented the same three conditions and `HomePage` already drew it.
 * Two implementations of one rule is how the two copies appeared, so the duplicate went rather than
 * only its renderer: a helper whose sole remaining readers are its own tests is the shape of thing
 * that quietly stops matching the screen.
 *
 * Pure, and separate from the shell, because the interesting part is *whether* the notice shows, and
 * that is a function, not a component.
 */

export interface CustomerNotice {
  /** `hours` is about today's opening; `general` is whatever the restaurant wants to say. */
  kind: 'hours' | 'general'
  message: string
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function flag(value: unknown): boolean {
  return value === true
}

/**
 * The notice from *Všeobecné nastavenia*, if it is switched on and says something.
 *
 * An enabled notice with an empty message is nothing to show — a bar with no text in it reads as a
 * rendering fault, which is worse than saying nothing.
 */
export function generalNotice(payload: unknown): CustomerNotice | null {
  const row = payload as { announcement?: unknown; announcementEnabled?: unknown } | null
  if (row === null || typeof row !== 'object') return null
  if (!flag(row.announcementEnabled)) return null
  const message = text(row.announcement)
  return message === '' ? null : { kind: 'general', message }
}
